import React, { useState } from 'react';
import { Box, Divider, Link, Typography } from '@mui/material';
import { useL2DTheme } from '@europrocurement/flexy-components';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { TiersContact } from '@europrocurement/l2d-modules/providers/TiersProvider';
import { PeoplePreview } from '@europrocurement/l2d-modules';

export type PrescriberOtherContactsProps = {
    contacts: TiersContact[];
};

const PAGE_SIZE = 5;

const PrescriberOtherContacts: React.FunctionComponent<PrescriberOtherContactsProps> = function ({
    contacts,
}) {
    const { palette } = useL2DTheme();
    const [showAll, setShowAll] = useState(false);

    const sorted = [...contacts].sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        return 0;
    });

    const displayed = showAll ? sorted : sorted.slice(0, PAGE_SIZE);
    const remaining = sorted.length - PAGE_SIZE;

    return (
        <Box>
            <Divider />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: '32px',
                    py: 1.5,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: palette.text.secondary,
                    }}
                >
                    Autres contacts
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: palette.text.secondary }}
                >
                    {contacts.length}
                </Typography>
            </Box>

            {displayed.map((contact) => {
                const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ');

                return (
                    <Box
                        key={contact.id}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: '32px',
                            py: 1,
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: palette.action.hover },
                        }}
                    >
                        <PeoplePreview
                            firstName={contact.firstName}
                            lastName={contact.lastName}
                            sxAvatar={{
                                width: 32,
                                height: 32,
                            }}
                            auth={false}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {fullName}
                                </Typography>
                                {!contact.active && (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: palette.text.secondary, flexShrink: 0 }}
                                    >
                                        (inactif)
                                    </Typography>
                                )}
                            </Box>
                            {contact.fonction && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    display="block"
                                >
                                    {contact.fonction}
                                </Typography>
                            )}
                        </Box>
                        <FaOptionIcon
                            icon={['fasl', 'chevron-right']}
                            sx={{ fontSize: '12px', color: palette.text.secondary, flexShrink: 0 }}
                        />
                    </Box>
                );
            })}

            <Box sx={{ px: '32px', pb: 2, mt: sorted.length > PAGE_SIZE ? 1 : 0 }}>
                {sorted.length > PAGE_SIZE && (
                    <Link
                        component="button"
                        variant="caption"
                        onClick={() => setShowAll((prev) => !prev)}
                        sx={{ display: 'block', textAlign: 'center', width: '100%' }}
                    >
                        {showAll ? 'Afficher moins' : `+ Afficher les ${remaining} autres contacts`}
                    </Link>
                )}
            </Box>
        </Box>
    );
};

export default PrescriberOtherContacts;
